import React, {useEffect, useRef, useState} from "react";
import {
  TextField,
  Button,
  Container,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel, MenuItem, Select, InputLabel, FormControl, IconButton, Autocomplete, Popover
} from "@mui/material";
import ReCAPTCHA from "react-google-recaptcha";
import axios from "axios";
import {useTranslation} from "react-i18next";
import Header from "../common/Header";
import {buttonStyle, inputStyle} from "../styles";
import {useNavigate} from "react-router-dom";
import {BASIC_URL, companySizes, companyTypes} from "../enum";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const RECAPTCHA_SITE_KEY = "6LcEP5IrAAAAADtKwnVX-t8k_EpmO2bkDG5cNxMW";
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SignUp = () => {
  const {t, i18n} = useTranslation();
  const navigate = useNavigate();
  const alertRef = useRef(null);

  const [formData, setFormData] = useState({
    salutation: null,
    firstName: "",
    lastName: "",
    email: "",
    companyName: "",
    companyType: "",
    companySize: "",
    newsletterAgreed: null,
    dataProcessingAgreed: null
  });

  const [captchaToken, setCaptchaToken] = useState(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handlePopoverOpen = (event) => setAnchorEl(event.currentTarget);
  const handlePopoverClose = () => setAnchorEl(null);
  const popoverOpen = Boolean(anchorEl);

  useEffect(() => {
    const token = localStorage.getItem("jwt");
    if (token) {
      navigate("/company");
    }
  }, [navigate]);

  const handleChange = (e) => {
    const {name, value} = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value === "" ? null : value,
    }));
  };

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  const scrollToAlert = () => {
    setTimeout(() => {
      if (alertRef.current) {
        alertRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 50);
  };

  const validateFields = () => {
    const errors = {};

    if (!formData.firstName) errors.firstName = true;
    if (!formData.lastName) errors.lastName = true;
    if (!formData.email) errors.email = true;
    if (!formData.companyName) errors.companyName = true;
    if (!formData.companyType) errors.companyType = true;
    if (!formData.companySize) errors.companySize = true;
    if (!formData.dataProcessingAgreed) errors.dataProcessingAgreed = true;
    if (!captchaToken) errors.captchaToken = true;

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess(false);
    setError("");

    const errors = validateFields();
    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setError(t("sign_up.error.required"));
      scrollToAlert();
      return;
    }

    if (!emailRegex.test(formData.email)) {
      setFieldErrors((prev) => ({ ...prev, email: true }));
      setError(t("sign_up.error.invalidEmail"));
      scrollToAlert();
      return;
    }

    setLoading(true);

    try {
      const emailCheck = await axios.post(`${BASIC_URL}/email-exists`, {
        email: formData.email,
      });

      if (emailCheck.data.exists) {
        setError(t("sign_up.error.emailExists"));
        setLoading(false);
        scrollToAlert();
        return;
      }

      const companyCheck = await axios.post(`${BASIC_URL}/company-exists`, {
        company: formData.companyName,
      });

      if (companyCheck.data.exists) {
        setError(t("sign_up.error.companyExists"));
        setLoading(false);
        scrollToAlert();
        return;
      }

      const res = await axios.post(`${BASIC_URL}/api/company-requests`, {
        data: {
          Vorname: formData.firstName,
          Nachname: formData.lastName,
          Email: formData.email,
          Newsletter_zugestimmt: formData.newsletterAgreed,
          Unternehmen: formData.companyName,
          Anrede: formData.salutation,
          Rechtsform: formData.companyType,
          Unternehmensgroesse: formData.companySize,
          captchaToken
        },
      });

      if (res.status === 200 || res.status === 201) {
        setSuccess(true);
        setFormData({
          salutation: null,
          firstName: "",
          lastName: "",
          email: "",
          companyName: "",
          newsletterAgreed: null,
          dataProcessingAgreed: null,
        });
        setCaptchaToken(null);
      }
    } catch (err) {
      console.error(err);
      setError(t("sign_up.error.submit"));
    } finally {
      setLoading(false);
      scrollToAlert();
    }
  };

  return (
    <>
      <Container maxWidth="sm" sx={{ pb: 3 }}>
        <Box mt={4} mb={3}>
          <Typography variant="h4" align="center" gutterBottom>
            {t("sign_up.title")}
          </Typography>
          <Typography variant="body1" align="center">
            {t("sign_up.subtitle")}
          </Typography>
        </Box>

        <form onSubmit={handleSubmit} noValidate>
          <FormControl fullWidth margin="normal" sx={{backgroundColor: "#f7f8fa"}}>
            <InputLabel
              sx={{
                backgroundColor: "#f7f8fa",
                px: 0.5,
              }}
            >
              {t("sign_up.salutation")}
            </InputLabel>
            <Select
              name="salutation"
              value={formData.salutation ?? null}
              onChange={handleChange}
              sx={{
                backgroundColor: "#f7f8fa",
                borderRadius: 0,
                "& fieldset": {
                  borderColor: "#dce1e8",
                },
                "&:hover fieldset": {
                  borderColor: "#0063a6",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#0063a6",
                  borderWidth: 2,
                },
              }}
            >
              <MenuItem value={null}>
                <em>{t("sign_up.select_placeholder")}</em>
              </MenuItem>
              <MenuItem value="Herr">{t("sign_up.salutation_herr")}</MenuItem>
              <MenuItem value="Frau">{t("sign_up.salutation_frau")}</MenuItem>
              <MenuItem value="Divers">{t("sign_up.salutation_divers")}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="normal"
            label={t("sign_up.firstName")}
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
            error={!!fieldErrors.firstName}
            sx={inputStyle}
          />
          <TextField
            fullWidth
            margin="normal"
            label={t("sign_up.lastName")}
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
            error={!!fieldErrors.lastName}
            sx={inputStyle}
          />
          <TextField
            fullWidth
            margin="normal"
            label={t("sign_up.email")}
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            error={!!fieldErrors.email}
            sx={inputStyle}
          />
          <TextField
            fullWidth
            margin="normal"
            label={t("sign_up.company")}
            name="companyName"
            value={formData.companyName}
            onChange={handleChange}
            required
            error={!!fieldErrors.companyName}
            sx={inputStyle}
          />
          <Autocomplete
            options={companyTypes}
            getOptionLabel={(option) => option}
            value={formData.companyType}
            onChange={(event, value) =>
              setFormData((prev) => ({
                ...prev,
                companyType: value,
              }))
            }
            renderInput={(params) => (
              <TextField {...params} required error={!!fieldErrors.companyType} margin="normal" label={t('company_form.company_type')} sx={inputStyle} />
            )}
            fullWidth
          />

          <Autocomplete
            options={companySizes}
            getOptionLabel={(option) => t("company_size." + option)}
            value={formData.companySize || null}
            onChange={(event, value) =>
              setFormData((prev) => ({
                ...prev,
                companySize: value,
              }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                required
                error={!!fieldErrors.companySize}
                margin="normal"
                label={t('company_form.company_size')}
                sx={inputStyle}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {params.InputProps.endAdornment}
                      <IconButton size="small" onClick={handlePopoverOpen}>
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </>
                  ),
                }}
              />
            )}
            fullWidth
          />
          <Popover
            open={popoverOpen}
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <Box sx={{ p: 2, overflowX: 'auto' }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('company_form.size_overview')}
              </Typography>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontFamily: 'Roboto, sans-serif' }}>
                <thead>
                <tr>
                  <th style={{ padding: '8px', border: '1px solid #ccc', textAlign: 'left', minWidth: '100px' }}>{t('company_form.size')}</th>
                  <th style={{ padding: '8px', border: '1px solid #ccc', textAlign: 'left', minWidth: '120px' }}>{t('company_form.employees')}</th>
                  <th style={{ padding: '8px', border: '1px solid #ccc', textAlign: 'left', minWidth: '120px' }}>{t('company_form.revenue')}</th>
                  <th style={{ padding: '8px', border: '1px solid #ccc', textAlign: 'left', minWidth: '120px' }}>{t('company_form.balance')}</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td style={{ padding: '8px', border: '1px solid #ccc' }}>{t('company_form.size_small')}</td>
                  <td style={{ padding: '8px', border: '1px solid #ccc' }}>{t('company_form.employees_small')}</td>
                  <td style={{ padding: '8px', border: '1px solid #ccc' }}>{t('company_form.revenue_small')}</td>
                  <td style={{ padding: '8px', border: '1px solid #ccc' }}>{t('company_form.balance_small')}</td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', border: '1px solid #ccc' }}>{t('company_form.size_medium')}</td>
                  <td style={{ padding: '8px', border: '1px solid #ccc' }}>{t('company_form.employees_medium')}</td>
                  <td style={{ padding: '8px', border: '1px solid #ccc' }}>{t('company_form.revenue_medium')}</td>
                  <td style={{ padding: '8px', border: '1px solid #ccc' }}>{t('company_form.balance_medium')}</td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', border: '1px solid #ccc' }}>{t('company_form.size_large')}</td>
                  <td style={{ padding: '8px', border: '1px solid #ccc' }}>{t('company_form.employees_large')}</td>
                  <td style={{ padding: '8px', border: '1px solid #ccc' }}>{t('company_form.revenue_large')}</td>
                  <td style={{ padding: '8px', border: '1px solid #ccc' }}>{t('company_form.balance_large')}</td>
                </tr>
                </tbody>
              </table>
            </Box>
          </Popover>

          <FormControlLabel
            control={
              <Checkbox
                checked={!!formData.newsletterAgreed}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    newsletterAgreed: e.target.checked,
                  }))
                }
                sx={{
                  color: "#0063a6",
                  "&.Mui-checked": {
                    color: "#0063a6",
                  },
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}
              />
            }
            label={
              <Typography
                variant="body2"
                sx={{
                  color: "#333",
                  fontSize: "14px",
                  marginLeft: "10px",
                }}
              >
                {t("sign_up.newsletter")}
              </Typography>
            }
            sx={{
              alignItems: "flex-start",
              mb: 2,
              display: "flex",
              flexDirection: "row",
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={!!formData.dataProcessingAgreed}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    dataProcessingAgreed: e.target.checked,
                  }))
                }
                sx={{
                  border: fieldErrors.dataProcessingAgreed ? "1px solid #d32f2f" : "none",
                  color: "#0063a6",
                  "&.Mui-checked": {
                    color: "#0063a6",
                  },
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}
              />
            }
            label={
              <Typography
                variant="body2"
                sx={{
                  color: "#333",
                  fontSize: "14px",
                  marginLeft: "10px",
                }}
              >
                <span style={{color: "#d32f2f", marginRight: 4}}>*</span>
                {t('sign_up.dataProcessingPrefix')}
                <a
                  style={{ color: "#0063a6", textDecoration: "underline", cursor: "pointer" }}
                  href="https://www.gets.saarland/datenschutz/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('sign_up.dataProcessingLinkText')}
                </a>
                {t('sign_up.dataProcessingSuffix')}
              </Typography>
            }
            sx={{
              border: fieldErrors.dataProcessingAgreed ? "1px solid #d32f2f" : "none",
              borderRadius: 0,
              p: fieldErrors.dataProcessingAgreed ? 1 : 0,
              alignItems: "flex-start",
              mb: 2,
              display: "flex",
              flexDirection: "row",
            }}
          />


          <Box mt={2} display="flex" justifyContent="center">
            <ReCAPTCHA
              key={i18n.language}
              sitekey={RECAPTCHA_SITE_KEY}
              onChange={handleCaptchaChange}
              hl={i18n.language}
            />
          </Box>

          <Box mt={3} position="relative">
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={buttonStyle}
            >
              {t("sign_up.submit")}
            </Button>
            {loading && (
              <CircularProgress
                size={24}
                sx={{
                  position: "absolute",
                  top: "50%",
                  right: "16px",
                  marginTop: "-12px",
                }}
              />
            )}
          </Box>
        </form>

        {success && (
          <Box ref={alertRef} mt={2}>
            <Alert severity="success">{t("sign_up.success")}</Alert>
          </Box>
        )}
        {error && (
          <Box ref={alertRef} mt={2}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}
      </Container>
    </>
  );
};

export default SignUp;
